import { useMemo } from 'react';
import { Flex } from '@chakra-ui/react';
import { departmentColorFrom, departmentColorMap } from '@/utils/departmentColors';
import type { ChatListItem } from '../useCommunicationPage';
import { DepartmentCard } from './DepartmentCard';

/** Matches the px={4} the general card, section labels and private rows all use. */
const DEPARTMENT_ROW_INSET = '16px';

type Props = {
  items: ChatListItem[];
  selectedKey: string | null;
  onSelect: (item: ChatListItem) => void;
};

/**
 * The department chats for the selected event, as a horizontally scrolling row.
 *
 * Colours are assigned across the whole set at once, so no two departments on one event
 * share a tile colour, and a department keeps the colour it has on the Volunteers page.
 */
export function DepartmentCarousel({ items, selectedKey, onSelect }: Props) {
  const colors = useMemo(
    () => departmentColorMap(items.map((i) => i.title)),
    [items]
  );

  return (
    <Flex
      gap={3}
      pb={1}
      // Touch scrolls the row sideways; a desktop pane is wide enough to wrap the cards
      // into a grid instead, which uses the width rather than hiding cards off-screen.
      flexWrap={{ base: 'nowrap', lg: 'wrap' }}
      overflowX={{ base: 'auto', lg: 'visible' }}
      scrollSnapType="x proximity"
      css={{
        // The 16px inset lives here rather than in a px prop so it matches the other
        // sections. Two reasons it has to be done this way:
        //   - padding-inline-end on a flex scroll container is dropped by WebKit/Blink
        //     once the content overflows, so the last card ran to the screen edge. The
        //     ::after spacer is a real flex item and is laid out normally.
        //   - the css prop wins over style props, so a px prop on the element was being
        //     overridden by this block and the first card sat flush left.
        paddingInlineStart: DEPARTMENT_ROW_INSET,
        // Keeps a snapped card aligned to the inset rather than to the container edge.
        scrollPaddingInlineStart: DEPARTMENT_ROW_INSET,
        '&::after': { content: '""', flex: `0 0 ${DEPARTMENT_ROW_INSET}` },
        // Once the row wraps, that spacer would take a slot in the grid and push a card
        // onto its own line, so past lg the trailing inset goes back to plain padding.
        '@media (min-width: 62em)': {
          paddingInlineEnd: DEPARTMENT_ROW_INSET,
          '&::after': { display: 'none' },
        },
        // Momentum scrolling on touch; the scrollbar itself is noise beside three cards.
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {items.map((item) => (
        <DepartmentCard
          key={item.key}
          item={item}
          color={departmentColorFrom(colors, item.title)}
          active={selectedKey === item.key}
          onSelect={() => onSelect(item)}
        />
      ))}
    </Flex>
  );
}
